import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  CircularProgress,
  Alert,
  Chip,
  Select,
  MenuItem,
  FormControl,
  TextField,
  InputAdornment,
  alpha,
} from '@mui/material';
import { ArrowBack, AdminPanelSettings, Search } from '@mui/icons-material';
import { AppLayout } from '@/components/layout';
import { useAdminAccess, useUserReport, useUpdateUserPlan } from '@/hooks/useAdminReport';
import type { UserPlan } from '@/utils/planLimits';
import { brandColors } from '@/theme/colors';

type SortField = 'joinedAt' | 'email' | 'thinkSpaceCount' | 'todoListCount' | 'notebookCount' | 'storageUsedBytes';
type SortOrder = 'asc' | 'desc';

const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

const formatDate = (timestamp: { toDate?: () => Date } | null | undefined): string => {
  if (!timestamp) return '-';
  const date = timestamp.toDate ? timestamp.toDate() : new Date();
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

export const AdminUsersPage = () => {
  const navigate = useNavigate();
  const hasAccess = useAdminAccess();
  const { data: users, isLoading, error } = useUserReport();
  const updatePlanMutation = useUpdateUserPlan();

  const [sortField, setSortField] = useState<SortField>('joinedAt');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Redirect if no admin access
  if (!hasAccess) {
    return (
      <AppLayout showBackToDashboard title="Unauthorized">
        <Container maxWidth="md" sx={{ py: 4 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            You do not have permission to access this page.
          </Alert>
          <Button startIcon={<ArrowBack />} onClick={() => navigate('/dashboard')}>
            Back to Dashboard
          </Button>
        </Container>
      </AppLayout>
    );
  }

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  const handlePlanChange = async (userId: string, newPlan: UserPlan) => {
    try {
      await updatePlanMutation.mutateAsync({ userId, plan: newPlan });
    } catch (err) {
      console.error('Failed to update plan:', err);
    }
  };

  // Filter users by search query
  const filteredUsers = users
    ? users.filter((user) => {
        if (!searchQuery.trim()) return true;
        const query = searchQuery.toLowerCase();
        const email = user.email?.toLowerCase() || '';
        const name = user.displayName?.toLowerCase() || '';
        return email.includes(query) || name.includes(query);
      })
    : [];

  // Sort filtered users
  const sortedUsers = [...filteredUsers].sort((a, b) => {
    let comparison = 0;
    switch (sortField) {
      case 'joinedAt':
        comparison = (a.joinedAt?.toMillis?.() || 0) - (b.joinedAt?.toMillis?.() || 0);
        break;
      case 'email':
        comparison = a.email.localeCompare(b.email);
        break;
      case 'thinkSpaceCount':
        comparison = a.thinkSpaceCount - b.thinkSpaceCount;
        break;
      case 'todoListCount':
        comparison = a.todoListCount - b.todoListCount;
        break;
      case 'notebookCount':
        comparison = a.notebookCount - b.notebookCount;
        break;
      case 'storageUsedBytes':
        comparison = a.storageUsedBytes - b.storageUsedBytes;
        break;
    }
    return sortOrder === 'asc' ? comparison : -comparison;
  });

  // Paginate sorted users
  const paginatedUsers = sortedUsers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Handle page change
  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Reset page when search changes
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  if (isLoading) {
    return (
      <AppLayout showBackToDashboard title="Admin - Users">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress />
        </Box>
      </AppLayout>
    );
  }

  return (
    <AppLayout showBackToDashboard title="Admin - Users">
      <Container maxWidth="xl" sx={{ py: { xs: 2, md: 4 } }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
          <AdminPanelSettings sx={{ fontSize: 40, color: brandColors.purple }} />
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
              User Report
            </Typography>
            <Typography variant="body2" color="text.secondary">
              View and manage user accounts and plans
            </Typography>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            Failed to load user report: {(error as Error)?.message || 'Unknown error'}
          </Alert>
        )}

        {/* Summary Stats */}
        <Box sx={{ display: 'flex', gap: 2, mb: 4, flexWrap: 'wrap' }}>
          <Paper sx={{ p: 2, minWidth: 150 }}>
            <Typography variant="h4" sx={{ fontWeight: 600, color: brandColors.purple }}>
              {users?.length || 0}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Users
            </Typography>
          </Paper>
          <Paper sx={{ p: 2, minWidth: 150 }}>
            <Typography variant="h4" sx={{ fontWeight: 600, color: brandColors.accent.green }}>
              {users?.filter((u) => u.plan === 'pro').length || 0}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Pro Users
            </Typography>
          </Paper>
          <Paper sx={{ p: 2, minWidth: 150 }}>
            <Typography variant="h4" sx={{ fontWeight: 600 }}>
              {users?.filter((u) => u.plan === 'free').length || 0}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Free Users
            </Typography>
          </Paper>
        </Box>

        {/* Search */}
        <Box sx={{ mb: 2 }}>
          <TextField
            placeholder="Search by name or email..."
            value={searchQuery}
            onChange={handleSearchChange}
            size="small"
            sx={{ width: { xs: '100%', sm: 300 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search color="action" />
                </InputAdornment>
              ),
            }}
          />
          {searchQuery && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Showing {filteredUsers.length} of {users?.length || 0} users
            </Typography>
          )}
        </Box>

        {/* Users Table */}
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: alpha(brandColors.purple, 0.05) }}>
                <TableCell>
                  <TableSortLabel
                    active={sortField === 'email'}
                    direction={sortField === 'email' ? sortOrder : 'asc'}
                    onClick={() => handleSort('email')}
                  >
                    User
                  </TableSortLabel>
                </TableCell>
                <TableCell>Plan</TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortField === 'joinedAt'}
                    direction={sortField === 'joinedAt' ? sortOrder : 'asc'}
                    onClick={() => handleSort('joinedAt')}
                  >
                    Joined
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">
                  <TableSortLabel
                    active={sortField === 'thinkSpaceCount'}
                    direction={sortField === 'thinkSpaceCount' ? sortOrder : 'asc'}
                    onClick={() => handleSort('thinkSpaceCount')}
                  >
                    Spaces
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">
                  <TableSortLabel
                    active={sortField === 'todoListCount'}
                    direction={sortField === 'todoListCount' ? sortOrder : 'asc'}
                    onClick={() => handleSort('todoListCount')}
                  >
                    ToDo Lists
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">
                  <TableSortLabel
                    active={sortField === 'notebookCount'}
                    direction={sortField === 'notebookCount' ? sortOrder : 'asc'}
                    onClick={() => handleSort('notebookCount')}
                  >
                    Notebooks
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sortField === 'storageUsedBytes'}
                    direction={sortField === 'storageUsedBytes' ? sortOrder : 'asc'}
                    onClick={() => handleSort('storageUsedBytes')}
                  >
                    Storage
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedUsers.map((user) => (
                <TableRow key={user.uid} hover>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {user.displayName || user.email}
                      </Typography>
                      {user.displayName && (
                        <Typography variant="caption" color="text.secondary">
                          {user.email}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <FormControl size="small" variant="standard">
                      <Select
                        value={user.plan}
                        onChange={(e) => handlePlanChange(user.uid, e.target.value as UserPlan)}
                        disabled={updatePlanMutation.isPending}
                        sx={{ minWidth: 80 }}
                      >
                        <MenuItem value="free">
                          <Chip label="Free" size="small" variant="outlined" />
                        </MenuItem>
                        <MenuItem value="pro">
                          <Chip
                            label="Pro"
                            size="small"
                            sx={{
                              bgcolor: alpha(brandColors.accent.green, 0.1),
                              color: brandColors.accent.green,
                              fontWeight: 600,
                            }}
                          />
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(user.joinedAt)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2">{user.thinkSpaceCount}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2">{user.todoListCount}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2">{user.notebookCount}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">{formatBytes(user.storageUsedBytes)}</Typography>
                  </TableCell>
                </TableRow>
              ))}
              {paginatedUsers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      {searchQuery ? 'No users match your search' : 'No users found'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={sortedUsers.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </TableContainer>
      </Container>
    </AppLayout>
  );
};
